import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell,
    LayoutDashboard,
    CalendarDays,
    Layers3,
    ClipboardCheck,
    GraduationCap,
    Wallet,
    BookOpen,
    Users,
    Settings,
    Building2,
    ShieldCheck,
    UserCog,
    ScrollText,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ShoppingCart,
    Boxes,
    BarChart3,
    Truck,
    FileText,
    Package,
    Wrench,
    KeyRound,
    ShieldAlert,
    LifeBuoy,
    Trash2,
    Calendar,
    Mail,
    Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

type Item = {
    to: string;
    labelKey: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    perm?: string;
    activity?: string; // gate: only visible when tenant.activity_types contains this
    module?: string;   // gate: only visible when tenant plan.modules contains this
    children?: Item[];
};

const NAV: Item[] = [
    { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, module: "dashboard" },
    { to: "/people", labelKey: "nav.people", icon: Users, perm: "people.view", module: "people" },
    { to: "/programs", labelKey: "nav.programs", icon: BookOpen, perm: "program.view", module: "programs" },
    { to: "/groups", labelKey: "nav.groups", icon: Layers3, perm: "group.view", module: "groups" },
    { to: "/schedule", labelKey: "nav.schedule", icon: CalendarDays, perm: "schedule.view", module: "schedule" },
    { to: "/tasks", labelKey: "nav.tasks", icon: ClipboardCheck, perm: "tasks.view", module: "tasks" },
    { to: "/exams", labelKey: "nav.exams", icon: GraduationCap, perm: "exam.view", module: "exams" },
    {
        to: "/hr", labelKey: "nav.hr", icon: UserCog, perm: "hr.view", module: "hr",
        children: [
            { to: "/hr", labelKey: "nav.hr_employees", icon: UserCog, perm: "hr.view", module: "hr" },
            { to: "/hr/payroll", labelKey: "nav.hr_payroll", icon: Wallet, perm: "payroll.view", module: "hr.payroll" },
            { to: "/hr/payroll/runs", labelKey: "nav.hr_payroll_runs", icon: ScrollText, perm: "payroll.view", module: "hr.payroll" },
        ],
    },
    { to: "/crm", labelKey: "nav.crm", icon: Users, perm: "crm.view", module: "crm" },
    { to: "/support", labelKey: "nav.support", icon: LifeBuoy, perm: "support.use", module: "support" },
    { to: "/trash", labelKey: "nav.trash", icon: Trash2, perm: "trash.manage", module: "trash" },
    {
        to: "/kindergarten", labelKey: "nav.kindergarten", icon: BookOpen, perm: "kg.view", activity: "kindergarten", module: "kindergarten",
        children: [
            { to: "/kindergarten", labelKey: "nav.kg_dashboard", icon: BookOpen, perm: "kg.view", module: "kindergarten" },
            { to: "/kindergarten/incidents", labelKey: "nav.kg_incidents", icon: ScrollText, perm: "kg.view", module: "kindergarten" },
        ],
    },
    {
        to: "/finance", labelKey: "nav.finance", icon: Wallet, module: "finance",
        children: [
            // Phase UI-2: "Fakturalar" was renamed to "Gələcək mədaxil" via
            // the i18n key `nav.invoices`; the route stays at
            // /finance/invoices so all deep-links keep resolving. The
            // "Avto-fakturalaşma" child was removed from the tenant UI —
            // backend endpoints still exist, they are just no longer
            // surfaced in the sidebar, module catalog or plan features.
            { to: "/finance/invoices", labelKey: "nav.invoices", icon: ScrollText, perm: "finance.view", module: "finance.invoices" },
            { to: "/finance/payments", labelKey: "nav.payments", icon: Wallet, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/debt", labelKey: "nav.debt", icon: ScrollText, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/cash", labelKey: "nav.cash", icon: Wallet, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/other-income", labelKey: "nav.other_income", icon: Wallet, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/pricing-plans", labelKey: "nav.pricing_plans", icon: ScrollText, perm: "finance.view", module: "finance.invoices" },
        ],
    },
    {
        to: "/procurement", labelKey: "nav.procurement", icon: ShoppingCart, module: "procurement",
        children: [
            { to: "/procurement/requests", labelKey: "nav.procurement_requests", icon: FileText, perm: "procurement.view", module: "procurement" },
            { to: "/procurement/vendors", labelKey: "nav.procurement_vendors", icon: Truck, perm: "procurement.view", module: "procurement" },
        ],
    },
    {
        to: "/inventory", labelKey: "nav.inventory", icon: Boxes, module: "inventory",
        children: [
            { to: "/inventory/items", labelKey: "nav.inventory_items", icon: Package, perm: "inventory.view", module: "inventory" },
            { to: "/inventory/movements", labelKey: "nav.inventory_movements", icon: ScrollText, perm: "inventory.view", module: "inventory" },
            { to: "/inventory/equipment", labelKey: "nav.inventory_equipment", icon: Wrench, perm: "inventory.view", module: "inventory" },
        ],
    },
    { to: "/reports", labelKey: "nav.reports", icon: BarChart3, perm: "reports.view", module: "reports" },
    {
        to: "/settings", labelKey: "nav.settings", icon: Settings, module: "settings",
        children: [
            { to: "/settings/branches", labelKey: "nav.branches", icon: Building2, perm: "branch.view", module: "settings.branches" },
            { to: "/settings/rooms", labelKey: "nav.rooms", icon: Building2, perm: "room.view", module: "settings.rooms" },
            { to: "/settings/roles", labelKey: "nav.roles", icon: ShieldCheck, perm: "role.view", module: "settings.roles" },
            { to: "/settings/staff", labelKey: "nav.staff", icon: UserCog, perm: "staff.view", module: "settings.staff" },
            { to: "/settings/institution", labelKey: "nav.institution", icon: Building2, perm: "institution.view", module: "settings.institution" },
            { to: "/settings/crm", labelKey: "nav.crm_settings", icon: Users, perm: "crm.view", module: "crm" },
            { to: "/settings/audit", labelKey: "nav.audit", icon: ScrollText, perm: "audit.view", module: "settings.audit" },
            { to: "/settings/notifications", labelKey: "nav.notifications", icon: Bell, perm: "institution.view", module: "settings.institution" },
            // Phase UI-2: Təhlükəsizlik, Geliştirici, Məxfilik, İnteqrasiyalar
            // removed from tenant UI. Direct visits fall through to the
            // dashboard catch-all in App.tsx, and the modules are gone from
            // module_catalog so package feature lists never mention them.
        ],
    },
];

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
    const { t } = useTranslation();
    const { hasPerm, activeMembership, hasModule, hasModuleAccess, me } = useAuth();
    const [open, setOpen] = useState<Record<string, boolean>>({ "/settings": true });

    const acts = activeMembership?.tenant.activity_types || [];
    const isPO = !!me?.user?.is_platform_owner;
    const canSee = (i: Item) =>
        (!i.perm || hasPerm(i.perm)) &&
        (!i.activity || acts.includes(i.activity)) &&
        // Phase TAC-1: per-tenant module policy (parent + submodule).
        // Platform owners always see; otherwise module.view must be enabled.
        (!i.module || isPO || hasModuleAccess(i.module, "view"));
    // Platform owner gets a top-level entry pointing to /platform (tenants, signup
    // requests, metrics, finance, staff, overrides). Hidden for every other role.
    const NAV_FINAL: Item[] = isPO
        ? [{ to: "/platform", labelKey: "nav.platform", icon: Globe }, ...NAV]
        : NAV;
    const visibleTop = NAV_FINAL.filter((i) => (i.children ? (canSee(i) && i.children.some(canSee)) : canSee(i)));

    return (
        <aside data-testid="sidebar" className="surface-menu shrink-0 h-screen sticky top-0 hidden md:flex flex-col transition-[width] duration-200" style={{ width: collapsed ? 72 : 248 }}>
            <div className="flex items-center gap-2 px-4" style={{ height: 64 }}>
                <Logo variant="icon" height={32} testId="brand-mark" />
                {!collapsed && (
                    <div className="flex flex-col leading-tight">
                        <span style={{ color: "var(--text)" }} className="text-sm font-semibold tracking-tight" data-testid="brand-name">Service LAB</span>
                    </div>
                )}
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-2" aria-label="primary">
                {visibleTop.map((item) => {
                    const Icon = item.icon;
                    if (item.children) {
                        const isOpen = !!open[item.to];
                        const visibleChildren = item.children.filter(canSee);
                        return (
                            <div key={item.to} className="mb-0.5">
                                <button
                                    data-testid={`${item.labelKey.replace(/\./g, "-")}`}
                                    onClick={() => setOpen((v) => ({ ...v, [item.to]: !v[item.to] }))}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors hover:bg-[var(--selected-bg)]"
                                    style={{ color: "var(--text-muted)", minHeight: 44 }}
                                    title={collapsed ? t(item.labelKey) : undefined}
                                >
                                    <Icon size={18} className="shrink-0" />
                                    {!collapsed && <><span className="truncate flex-1 text-left">{t(item.labelKey)}</span><ChevronDown size={14} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} /></>}
                                </button>
                                {!collapsed && isOpen && (
                                    <div className="ml-6 mt-1 border-l pl-2" style={{ borderColor: "var(--border)" }}>
                                        {visibleChildren.map((c) => (
                                            <NavLink key={c.to} to={c.to} end
                                                className={({ isActive }) => `w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors hover:bg-[var(--selected-bg)]`}
                                                style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--text-muted)", background: isActive ? "var(--selected-bg)" : "transparent" })}
                                                data-testid={`${c.labelKey.replace(/\./g, "-")}`}
                                            >
                                                <c.icon size={16} />
                                                <span className="truncate">{t(c.labelKey)}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <NavLink key={item.to} to={item.to} end
                            className={() => `mb-0.5 w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors hover:bg-[var(--selected-bg)]`}
                            style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--text-muted)", background: isActive ? "var(--selected-bg)" : "transparent", minHeight: 44 })}
                            data-testid={`${item.labelKey.replace(/\./g, "-")}`}
                            title={collapsed ? t(item.labelKey) : undefined}
                        >
                            <Icon size={18} className="shrink-0" />
                            {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                        </NavLink>
                    );
                })}
            </nav>
            <button data-testid="sidebar-toggle" onClick={onToggle} className="border-t px-4 py-3 flex items-center gap-2 text-sm hover:bg-[var(--selected-bg)] transition-colors" style={{ color: "var(--text-muted)" }}>
                {collapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /><span>{t("sidebar.collapse")}</span></>}
            </button>
        </aside>
    );
}
